import React from "react";
import { Link, useNavigate } from "react-router-dom";
import { ShoppingBag } from "lucide-react";
import { useCart } from "App";
import { toast } from "sonner";

const ProductCard = ({ product }) => {
  const { addToCart } = useCart();
  const navigate = useNavigate();

  const availableColors = [...new Set(product.variants?.map(v => v.color).filter(Boolean))];
  const totalStock = product.variants?.reduce((sum, v) => sum + v.stock, 0) || 0;
  const hasMultipleVariants = product.variants?.length > 1;

  const handleAddToCart = async (e) => {
    e.preventDefault();
    e.stopPropagation();

    if (hasMultipleVariants) {
      navigate(`/product/${product.product_id}`);
      return;
    }

    const variant = product.variants?.[0];
    const success = await addToCart(
      product.product_id, 
      1, 
      variant?.size, 
      variant?.color, 
      variant?.variant_id
    );
    if (success) {
      toast.success(`${product.name} added to cart`, {
        action: {
          label: "Go to Cart",
          onClick: () => navigate("/cart"),
        },
      });
    } else {
      toast.error("Failed to add to cart");
    }
  };

  const formatPrice = (price) => {
    return new Intl.NumberFormat("en-KE", {
      style: "currency",
      currency: "KES",
      minimumFractionDigits: 0,
    }).format(price);
  };

  return (
    <Link
      to={`/product/${product.product_id}`}
      className="group block card-product"
      data-testid={`product-card-${product.product_id}`}
    >
      {/* Image Container */}
      <div className="relative product-image-container aspect-[3/4] bg-transparent overflow-hidden transition-shadow duration-500 group-hover:shadow-xl rounded-sm">
        <img
          src={
            product.images?.[0].url ||
            "https://images.unsplash.com/photo-1596484552993-aec4311d3381?w=400&h=500&fit=crop"
          }
          alt={product.name}
          className="product-image w-full h-full object-cover transition-transform duration-700 group-hover:scale-[1.03]"
        />

        {/* Badges */}
        <div className="absolute top-3 left-3 flex flex-col gap-2">
          {/* {product.is_featured && (
            <span className="bg-[#8DA399] text-white text-xs px-3 py-1 rounded-full">
              Featured
            </span>
          )} */}
          {totalStock <= 1 && totalStock > 0 && (
            <span className="bg-[#E57373] text-white text-xs px-3 py-1 rounded-full">
              Low Stock
            </span>
          )}
          {totalStock === 0 && (
            <span className="bg-[#7D7D7D] text-white text-xs px-3 py-1 rounded-full">
              Sold Out
            </span>
          )}
        </div>

        {/* Add to Cart Button */}
        <button
          onClick={handleAddToCart}
          className="add-to-cart-btn absolute bottom-4 left-4 right-4 bg-white/95 backdrop-blur-sm text-[#4F3535] py-3 flex items-center justify-center gap-2 hover:bg-[#6A4B4B] hover:text-white transition-all duration-300 shadow-lg opacity-100 lg:opacity-0 lg:group-hover:opacity-100 rounded-md"
          disabled={totalStock === 0}
          data-testid={`add-to-cart-${product.product_id}`}
        >
          <ShoppingBag className="w-4 h-4" />
          <span className="text-sm font-medium tracking-wide">
            {totalStock === 0 ? "Out of Stock" : hasMultipleVariants ? "Choose Options" : "Add to Cart"}
          </span>
        </button>
      </div>

      {/* Product Info */}
      <div className="pt-4 px-1">
        <h3 className="font-medium text-[#4F3535] group-hover:text-[#6A4B4B] transition-colors line-clamp-1">
          {product.name}
        </h3>
        <p className="text-[#4F3535] font-semibold mt-1 price-tag">
          {formatPrice(product.price)}
        </p>

        {/* Colors */}
        {availableColors.length > 0 && (
          <div className="flex items-center gap-1 mt-1">
            {availableColors.slice(0, 4).map((color, index) => (
              <span
                key={index}
                className="w-3 h-3 rounded-full border border-[#E5E0DC]"
                style={{ backgroundColor: color.toLowerCase() }}
                title={color}
              ></span>
            ))}
            {availableColors.length > 4 && (
              <span className="text-xs text-[#8A7B7B]">
                +{availableColors.length - 4}
              </span>
            )}
          </div>
        )}
      </div>
    </Link>
  );
};

export default ProductCard;
